import React, { useState, useRef } from 'react';
import { useColorTheme } from '../../context/ColorThemeContext';
import {
  Building2, Calendar, Clock, MapPin, Phone, Type, Image,
  Plus, Trash2, ChevronDown, ChevronUp, Sparkles, Loader2, Upload, Paintbrush
} from 'lucide-react';

const THEMES = [
  { id: 'temple_gold', name: 'Temple Gold', colors: ['#B8860B', '#FFD700', '#1A1A2E'] },
  { id: 'sacred_red', name: 'Sacred Red', colors: ['#DC143C', '#FF6B6B', '#1A1A2E'] },
  { id: 'nature_green', name: 'Nature Green', colors: ['#166534', '#4ADE80', '#0A1A0A'] },
  { id: 'royal_blue', name: 'Royal Blue', colors: ['#1E3A5F', '#3B82F6', '#0A0A2E'] },
  { id: 'festive_orange', name: 'Festive Orange', colors: ['#C2410C', '#F97316', '#1A1008'] },
  { id: 'modern_purple', name: 'Modern Purple', colors: ['#581C87', '#A855F7', '#1A0A2E'] },
  { id: 'cream_classic', name: 'Cream Classic', colors: ['#8B4513', '#D2691E', '#FFF8F0'] },
  { id: 'white_elegant', name: 'White Elegant', colors: ['#1a1a1a', '#333333', '#FFFFFF'] },
  { id: 'sky_light', name: 'Sky Light', colors: ['#1E40AF', '#2563EB', '#EFF6FF'] },
];

const BG_CATEGORIES = [
  { id: 'cultural_icons', label: 'Cultural Icons' },
  { id: 'temple', label: 'Temple' },
  { id: 'nature', label: 'Nature' },
  { id: 'abstract', label: 'Abstract' },
  { id: 'festivals', label: 'Festivals' },
  { id: 'marketing_ad', label: 'Marketing Ad' },
  { id: 'custom', label: 'Custom' },
];

function Section({ title, icon: Icon, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-200/50 dark:border-slate-800/50">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
        data-testid={`section-toggle-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-slate-400" />}
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">{title}</span>
        </div>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
      </button>
      {open && <div className="px-4 pb-4 space-y-3">{children}</div>}
    </div>
  );
}

function FieldInput({ label, value, onChange, placeholder, multiline, required }) {
  const cls = "w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-slate-800 dark:text-slate-200 placeholder-slate-400";
  return (
    <div>
      {label && (
        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
          {label} {required && <span className="text-red-400">*</span>}
        </label>
      )}
      {multiline ? (
        <textarea className={`${cls} resize-none`} rows={2} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      ) : (
        <input className={cls} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      )}
    </div>
  );
}

export default function PosterForm({
  formData, updateField, onGenerateBackground, isGeneratingBg, bgError,
  onAddImage, onAddText, freeFormElements, selectedElementId,
  onSelectElement, onUpdateElement, onRemoveElement
}) {
  const { colorTheme } = useColorTheme();
  const logoInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [bgCategory, setBgCategory] = useState('cultural_icons');
  const [bgPrompt, setBgPrompt] = useState('');

  const gradient = colorTheme === 'purple' ? 'from-purple-600 to-pink-600'
    : colorTheme === 'blue' ? 'from-blue-600 to-sky-500'
    : 'from-emerald-600 to-teal-500';

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/poster-builder/upload-image`, {
        method: 'POST', body: fd,
      });
      const data = await resp.json();
      if (data.url) updateField('logo_url', data.url);
    } catch (err) {
      console.error('Logo upload failed:', err);
    }
  };

  const [logoUrlInput, setLogoUrlInput] = useState('');
  const [isFetchingLogo, setIsFetchingLogo] = useState(false);
  const [logoFetchError, setLogoFetchError] = useState('');

  const handleFetchLogoFromUrl = async () => {
    const url = logoUrlInput.trim();
    if (!url) {
      setLogoFetchError('Enter a website URL');
      return;
    }
    setIsFetchingLogo(true);
    setLogoFetchError('');
    try {
      const sessionToken = localStorage.getItem('sessionToken') || '';
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/extract-website-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
        },
        body: JSON.stringify({ url }),
      });
      const data = await resp.json();
      const logo = data?.data?.logo_url || data?.data?.logo_absolute_url;
      if (logo) {
        updateField('logo_url', logo);
        setLogoUrlInput('');
      } else {
        setLogoFetchError('No logo found on that page');
      }
    } catch (err) {
      console.error('Fetch logo from URL failed:', err);
      setLogoFetchError('Failed to fetch logo. Try again.');
    } finally {
      setIsFetchingLogo(false);
    }
  };

  const handleImageAdd = (e) => {
    const file = e.target.files?.[0];
    if (file) onAddImage(file);
    e.target.value = '';
  };

  const addScheduleItem = () => {
    updateField('schedule', [...(formData.schedule || []), { label: '', time: '' }]);
  };

  const updateScheduleItem = (idx, field, value) => {
    const updated = [...formData.schedule];
    updated[idx] = { ...updated[idx], [field]: value };
    updateField('schedule', updated);
  };

  const removeScheduleItem = (idx) => {
    updateField('schedule', formData.schedule.filter((_, i) => i !== idx));
  };

  // Find selected free-form element for property editing
  const selectedEl = freeFormElements.find(el => el.id === selectedElementId);

  return (
    <div className="text-sm" data-testid="poster-form">
      {/* Organization */}
      <Section title="Organization" icon={Building2}>
        <FieldInput label="Organization Name" value={formData.org_name} onChange={v => updateField('org_name', v)} placeholder="e.g. Balarmalar Tamil School" required />
        <FieldInput label="Secondary Name (Any Language)" value={formData.org_name_secondary} onChange={v => updateField('org_name_secondary', v)} placeholder="e.g. பாலர்மலர் தமிழ் பள்ளி" />
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Logo</label>
          <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
          {formData.logo_url ? (
            <div className="flex items-center gap-2">
              <img src={formData.logo_url} alt="Logo" className="w-12 h-12 rounded-lg object-cover border border-slate-200 dark:border-slate-700" />
              <button onClick={() => updateField('logo_url', null)} className="text-xs text-red-500 hover:text-red-600">Remove</button>
              <button onClick={() => logoInputRef.current?.click()} className="text-xs text-blue-500 hover:text-blue-600">Change</button>
            </div>
          ) : (
            <button
              onClick={() => logoInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 text-slate-500 dark:text-slate-400 transition-colors"
              data-testid="logo-upload-btn"
            >
              <Upload className="w-4 h-4" /> Upload Logo
            </button>
          )}
          
          {/* Fetch logo from website URL (brand retention quick action) */}
          {!formData.logo_url && (
            <div className="mt-2">
              <div className="flex gap-1.5">
                <input
                  type="url"
                  value={logoUrlInput}
                  onChange={e => setLogoUrlInput(e.target.value)}
                  placeholder="...or paste website URL"
                  className="flex-1 px-3 py-2 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 text-slate-800 dark:text-slate-200 placeholder-slate-400"
                  data-testid="logo-url-input"
                />
                <button
                  onClick={handleFetchLogoFromUrl}
                  disabled={isFetchingLogo || !logoUrlInput.trim()}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    isFetchingLogo || !logoUrlInput.trim()
                      ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                      : `bg-gradient-to-r ${gradient} text-white hover:opacity-90`
                  }`}
                  data-testid="fetch-logo-btn"
                >
                  {isFetchingLogo ? '...' : 'Fetch'}
                </button>
              </div>
              {logoFetchError && <p className="text-[11px] text-red-500 mt-1">{logoFetchError}</p>}
            </div>
          )}
        </div>
      </Section>

      {/* Event Details */}
      <Section title="Event Details" icon={Calendar}>
        <FieldInput label="Event Title" value={formData.event_title} onChange={v => updateField('event_title', v)} placeholder="e.g. Tamil School Opening Ceremony" required />
        <FieldInput label="Subtitle / Tagline" value={formData.event_subtitle} onChange={v => updateField('event_subtitle', v)} placeholder="e.g. Registration Open from KG to Year 6" />
        <div className="grid grid-cols-2 gap-2">
          <FieldInput label="Date" value={formData.date} onChange={v => updateField('date', v)} placeholder="March 7th, 2026" />
          <FieldInput label="Time" value={formData.time} onChange={v => updateField('time', v)} placeholder="2:00 PM - 4:30 PM" />
        </div>
      </Section>

      {/* Schedule */}
      <Section title="Schedule / Program" icon={Clock} defaultOpen={false}>
        {(formData.schedule || []).map((item, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <input
              className="flex-1 px-2 py-1.5 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-200"
              value={item.label} onChange={e => updateScheduleItem(idx, 'label', e.target.value)} placeholder="Activity"
            />
            <input
              className="w-24 px-2 py-1.5 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-200"
              value={item.time} onChange={e => updateScheduleItem(idx, 'time', e.target.value)} placeholder="Time"
            />
            <button onClick={() => removeScheduleItem(idx)} className="p-1 text-red-400 hover:text-red-500">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button onClick={addScheduleItem} className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 font-medium" data-testid="add-schedule-btn">
          <Plus className="w-3.5 h-3.5" /> Add schedule item
        </button>
      </Section>

      {/* Venue */}
      <Section title="Venue & Contact" icon={MapPin} defaultOpen={false}>
        <FieldInput label="Venue Name" value={formData.venue_name} onChange={v => updateField('venue_name', v)} placeholder="Parramatta Public School" />
        <FieldInput label="Address / Directions" value={formData.venue_address} onChange={v => updateField('venue_address', v)} placeholder="Next to NSW Police HQ..." />
        <FieldInput label="Contact Info" value={formData.contact_info} onChange={v => updateField('contact_info', v)} placeholder="email@example.com or phone" />
        <FieldInput label="Footer Message" value={formData.footer_message} onChange={v => updateField('footer_message', v)} placeholder="All Are Welcome!" />
      </Section>

      {/* Free-form Elements */}
      <Section title="Images & Text Layers" icon={Image}>
        <div className="flex gap-2">
          <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageAdd} />
          <button
            onClick={() => imageInputRef.current?.click()}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-gradient-to-r ${gradient} text-white text-xs font-medium hover:opacity-90 transition-opacity`}
            data-testid="add-image-btn"
          >
            <Image className="w-3.5 h-3.5" /> Add Image
          </button>
          <button
            onClick={onAddText}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-medium hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
            data-testid="add-text-btn"
          >
            <Type className="w-3.5 h-3.5" /> Add Text
          </button>
        </div>

        {/* Element list */}
        {freeFormElements.length > 0 && (
          <div className="space-y-1 mt-2">
            {freeFormElements.map(el => (
              <div
                key={el.id}
                onClick={() => onSelectElement(el.id)}
                className={`flex items-center justify-between px-2 py-1.5 rounded-lg cursor-pointer text-xs transition-colors ${
                  selectedElementId === el.id
                    ? 'bg-blue-50 dark:bg-blue-900/20 ring-1 ring-blue-300 dark:ring-blue-700'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
                data-testid={`element-${el.id}`}
              >
                <span className="text-slate-600 dark:text-slate-400 truncate flex items-center gap-1.5">
                  {el.type === 'image' ? <Image className="w-3 h-3" /> : <Type className="w-3 h-3" />}
                  {el.type === 'image' ? 'Image' : (el.content || 'Text').substring(0, 20)}
                </span>
                <button onClick={(e) => { e.stopPropagation(); onRemoveElement(el.id); }} className="p-0.5 text-red-400 hover:text-red-500">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Selected element properties */}
        {selectedEl && (
          <div className="mt-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 space-y-2" data-testid="element-properties">
            {selectedEl.type === 'text' && (
              <>
                <FieldInput label="Text Content" value={selectedEl.content || ''} onChange={v => onUpdateElement(selectedEl.id, { content: v })} multiline />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Font Size</label>
                    <input type="number" min={10} max={120} value={selectedEl.fontSize || 28}
                      onChange={e => onUpdateElement(selectedEl.id, { fontSize: parseInt(e.target.value) || 28 })}
                      className="w-full px-2 py-1.5 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-200"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Color</label>
                    <input type="color" value={selectedEl.fontColor || '#FFFFFF'}
                      onChange={e => onUpdateElement(selectedEl.id, { fontColor: e.target.value })}
                      className="w-full h-8 rounded-lg cursor-pointer border border-slate-200 dark:border-slate-700"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Font</label>
                  <select value={selectedEl.fontFamily || 'sans-serif'}
                    onChange={e => onUpdateElement(selectedEl.id, { fontFamily: e.target.value })}
                    className="w-full px-2 py-1.5 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-200"
                  >
                    <option value="sans-serif">Sans Serif</option>
                    <option value="serif">Serif</option>
                    <option value="monospace">Monospace</option>
                    <option value="'Noto Sans Tamil', sans-serif">Tamil (Noto Sans)</option>
                    <option value="cursive">Cursive</option>
                  </select>
                </div>
              </>
            )}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">Opacity</label>
              <input type="range" min={0} max={1} step={0.05} value={selectedEl.opacity ?? 1}
                onChange={e => onUpdateElement(selectedEl.id, { opacity: parseFloat(e.target.value) })}
                className="w-full"
              />
            </div>
          </div>
        )}
      </Section>

      {/* Visual Theme */}
      <Section title="Visual Theme" icon={Paintbrush} defaultOpen={true}>
        <div className="grid grid-cols-3 gap-2">
          {THEMES.map(t => (
            <button
              key={t.id}
              onClick={() => { updateField('theme', t.id); updateField('bg_color', null); }}
              className={`p-1.5 rounded-lg border-2 transition-all ${
                formData.theme === t.id
                  ? 'border-blue-500 shadow-md scale-105'
                  : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600'
              }`}
              data-testid={`theme-${t.id}`}
            >
              <div className="h-8 rounded-md mb-1" style={{
                background: `linear-gradient(135deg, ${t.colors[2]}, ${t.colors[0]}, ${t.colors[1]})`
              }} />
              <span className="text-[10px] font-medium text-slate-600 dark:text-slate-400">{t.name}</span>
            </button>
          ))}
        </div>
        {/* Custom background color */}
        <div className="mt-2 pt-2 border-t border-slate-200/50 dark:border-slate-700/50">
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">Custom Background Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={formData.bg_color || '#1A1A2E'}
              onChange={e => updateField('bg_color', e.target.value)}
              className="w-10 h-8 rounded-lg cursor-pointer border border-slate-200 dark:border-slate-700"
              data-testid="custom-bg-color"
            />
            <span className="text-xs text-slate-500 dark:text-slate-400 font-mono">{formData.bg_color || 'Using theme default'}</span>
            {formData.bg_color && (
              <button
                onClick={() => updateField('bg_color', null)}
                className="text-xs text-red-400 hover:text-red-500 ml-auto"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </Section>

      {/* AI Background */}
      <Section title="AI Background" icon={Sparkles} defaultOpen={false}>
        <div className="flex flex-wrap gap-1.5">
          {BG_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => setBgCategory(cat.id)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                bgCategory === cat.id
                  ? `bg-gradient-to-r ${gradient} text-white`
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
              data-testid={`bg-cat-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>
        {bgCategory === 'custom' && (
          <FieldInput value={bgPrompt} onChange={setBgPrompt} placeholder="Describe the background you want..." multiline />
        )}
        <button
          onClick={() => onGenerateBackground(bgCategory, bgPrompt)}
          disabled={isGeneratingBg}
          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-gradient-to-r ${gradient} text-white text-xs font-semibold hover:opacity-90 transition-opacity disabled:opacity-50`}
          data-testid="generate-bg-btn"
        >
          {isGeneratingBg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {isGeneratingBg ? 'Generating...' : 'Generate AI Background'}
        </button>
        {bgError && (
          <div className="mt-2 p-2 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-[11px] text-red-700 dark:text-red-300" data-testid="bg-error-msg">
            ⚠️ {bgError}
          </div>
        )}
        {formData.ai_background_url && (
          <div className="relative">
            <img src={formData.ai_background_url} alt="AI Background" className="w-full h-20 object-cover rounded-lg" />
            <button
              onClick={() => updateField('ai_background_url', null)}
              className="absolute top-1 right-1 p-0.5 bg-red-500/80 rounded-full text-white hover:bg-red-600"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </Section>
    </div>
  );
}
